// Una linea de «Lo que sabe tu agente», con lo que se puede hacer con ella
// (4.4, R8).
//
// **Corregir y retirar, y nada mas.** No hay «cambiar el tipo»: el tipo lo
// decide el motor a partir de lo que se dijo, y ofrecerlo aqui seria
// devolverle al cliente la eleccion de categoria que D1 le quita. Si lo que
// hay que cambiar es el peso, se retira y se vuelve a enseñar.
//
// **Retirar pide motivo.** No es burocracia: sin el, dentro de seis meses
// nadie sabe por que se fue, y el sistema puede volver a proponer lo mismo.
//
// Sale del fichero de la lista para que la fila tenga sitio para sus dos
// estados sin que la lista se vuelva ilegible.

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Brain, FileText, Pencil, User } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  workspacesService,
  type WhatTheAgentKnows as Memoria,
} from '@/services/workspacesService'

/** El icono por tipo. Va por la clave interna **a proposito**: la clave es
 *  para el codigo y nunca se pinta; lo que se lee es `seLlama`. */
const ICONO: Record<string, typeof Brain> = {
  politica: Pencil,
  correccion: Pencil,
  persona: User,
  situacion: Brain,
  referencia: FileText,
}

export function MemoryRow({
  memoria,
  onCambiada,
}: {
  memoria: Memoria
  onCambiada: () => void
}) {
  const { t } = useTranslation()
  const [corrigiendo, setCorrigiendo] = useState(false)
  const [retirando, setRetirando] = useState(false)
  const [linea, setLinea] = useState(memoria.linea)
  const [motivo, setMotivo] = useState('')
  const [trabajando, setTrabajando] = useState(false)

  const Icono = ICONO[memoria.tipo] ?? Brain

  const corregir = async () => {
    setTrabajando(true)
    try {
      await workspacesService.corregirMemoria(memoria.id, linea.trim())
      setCorrigiendo(false)
      onCambiada()
    } finally {
      setTrabajando(false)
    }
  }

  const retirar = async () => {
    setTrabajando(true)
    try {
      await workspacesService.retirarMemoria(memoria.id, {
        por: t('knowledge.lo_que_sabe.firma'),
        motivo: motivo.trim(),
      })
      setRetirando(false)
      onCambiada()
    } finally {
      setTrabajando(false)
    }
  }

  return (
    <li className="flex items-start gap-2">
      <Icono className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
      <div className="min-w-0 flex-1 space-y-1">
        {corrigiendo ? (
          <div className="space-y-2">
            <Textarea
              value={linea}
              onChange={(e) => setLinea(e.target.value)}
              aria-label={t('knowledge.lo_que_sabe.corregir')}
              rows={2}
            />
            <div className="flex gap-2">
              <Button size="xs" disabled={!linea.trim() || trabajando} onClick={corregir}>
                {t('knowledge.lo_que_sabe.guardar')}
              </Button>
              <Button
                size="xs"
                variant="ghost"
                onClick={() => {
                  setCorrigiendo(false)
                  setLinea(memoria.linea)
                }}
              >
                {t('knowledge.lo_que_sabe.dejarlo')}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm">{memoria.linea}</p>
        )}

        {/* La procedencia debajo, en gris: es lo que permite desmentirla, y sin
            ella una memoria equivocada no se corrige, se sufre. */}
        <p className="text-xs text-muted-foreground">
          {memoria.seLlama} · {memoria.procedencia} · {memoria.cuando}
        </p>

        {retirando ? (
          <div className="flex flex-wrap items-center gap-2">
            <Input
              className="max-w-xs"
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder={t('knowledge.lo_que_sabe.por_que_se_va')}
              aria-label={t('knowledge.lo_que_sabe.por_que_se_va')}
            />
            <Button
              size="xs"
              variant="destructive"
              disabled={!motivo.trim() || trabajando}
              onClick={retirar}
            >
              {t('knowledge.lo_que_sabe.retirar')}
            </Button>
            <Button size="xs" variant="ghost" onClick={() => setRetirando(false)}>
              {t('knowledge.lo_que_sabe.dejarlo')}
            </Button>
          </div>
        ) : (
          !corrigiendo && (
            <div className="flex gap-2">
              <Button size="xs" variant="ghost" onClick={() => setCorrigiendo(true)}>
                {t('knowledge.lo_que_sabe.corregir')}
              </Button>
              <Button size="xs" variant="ghost" onClick={() => setRetirando(true)}>
                {t('knowledge.lo_que_sabe.retirar')}
              </Button>
            </div>
          )
        )}
      </div>
    </li>
  )
}
